import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { cn } from '@workspace/ui/lib/utils';

import logoSymbol from '~/assets/img/logo_cd_symbol_white.svg';
import { membershipStore } from '~/stores';

import {
  CLUB_PUBLIC_THRESHOLD_MINOR,
  clubEntryThresholdMinor,
  clubJoinCtaLine,
  clubPriceLine,
} from '../../domain/club';
import {
  clubCartCtaKey,
  type MembershipEntryPayload,
} from '../../domain/clubEntry';
import type { MembershipFormatters } from '../../domain/format';
import type { MembershipPlan } from '../../domain/types';
import { MEMBERSHIP_TEXT } from '../text';
import { formatCartMoney } from '../useCartMembership';
import { useMembershipFormatters } from '../useMembershipFormatters';
import { useViewedOnce } from '../useViewedOnce';
import { ClubHeroFigure } from './ClubOfferScreen';
import { CLUB_CLASS } from './clubStyles';
import {
  reportEntryViewed,
  useCartZoneMismatch,
  useClubDeliveryOffer,
  useClubOffer,
  useClubUpgradeOffer,
  useIsClubMember,
  useOpenClubJoin,
  useOpenClubUpgrade,
  useReservesClubEntry,
} from './useClubEntry';

const entryPayload = (
  source: MembershipEntryPayload['source'],
  plan: MembershipPlan | null,
): MembershipEntryPayload | null =>
  plan ? { source, planCode: plan.code } : null;

const thresholdText = (plan: MembershipPlan, f: MembershipFormatters) => {
  const threshold = clubEntryThresholdMinor(plan);
  return threshold === null ? null : f.money(threshold, plan.currency);
};

/**
 * Space of the home banner while the offer resolves, so the categories below
 * do not jump when it arrives: the pill height on desktop, the card height
 * on mobile. Rendered only when the banner is likely to show
 * (`useReservesClubEntry`).
 */
const ClubHomeBannerPlaceholder = () => (
  <div
    aria-hidden="true"
    className="desktop:mx-0 mx-4 desktop:h-28 h-[172px] animate-pulse desktop:rounded-full rounded-[16px] bg-[#f2f2f2]"
  />
);

interface ClubHomeBannerCardProps {
  plan: MembershipPlan;
  /** The member threshold, formatted. */
  threshold: string;
  formatters: MembershipFormatters;
  onJoin: () => void;
}

/** The home banner itself: the burgundy pill on desktop, a card on mobile. */
const ClubHomeBannerCard = ({
  plan,
  threshold,
  formatters,
  onJoin,
}: ClubHomeBannerCardProps) => {
  const { t } = useTranslation();
  const price = clubPriceLine(plan, formatters);
  const shortPrice = clubPriceLine(plan, formatters, 'short');
  const cta = clubJoinCtaLine(plan);

  return (
    <section
      className={cn(
        'desktop:mx-0 mx-4 flex desktop:h-28 desktop:flex-row flex-col desktop:items-center desktop:gap-5 gap-5 desktop:rounded-full rounded-[16px] p-4 desktop:py-0 desktop:pr-8 desktop:pl-6 text-white',
        CLUB_CLASS.burgundy,
        CLUB_CLASS.font,
      )}
    >
      <div className="flex min-w-0 flex-1 items-center desktop:gap-5 gap-4">
        <img
          src={logoSymbol}
          alt=""
          aria-hidden="true"
          className="desktop:size-16 size-12 shrink-0 object-contain"
        />
        <div className="flex min-w-0 flex-col gap-1">
          <p
            className={cn(
              CLUB_CLASS.heading,
              'desktop:hidden font-normal text-[18px] leading-6',
            )}
          >
            {t(
              plan.freeDeliveryThresholdMinor === 0
                ? MEMBERSHIP_TEXT.clubHomeTitleMobileAlways
                : MEMBERSHIP_TEXT.clubHomeTitleMobile,
              { threshold },
            )}
          </p>
          <p className="desktop:block hidden truncate font-semibold text-[24px] leading-8">
            {t(
              plan.freeDeliveryThresholdMinor === 0
                ? MEMBERSHIP_TEXT.clubHomeTitleAlways
                : MEMBERSHIP_TEXT.clubHomeTitle,
              { threshold },
            )}
          </p>
          <p
            className={cn(
              'desktop:block hidden text-[16px] leading-5',
              CLUB_CLASS.mutedOnDark,
            )}
          >
            {t(MEMBERSHIP_TEXT.clubHomePublic, {
              threshold: formatters.money(
                CLUB_PUBLIC_THRESHOLD_MINOR,
                plan.currency,
              ),
            })}
          </p>
        </div>
      </div>
      <div className="flex desktop:flex-row flex-col desktop:items-center desktop:gap-5 gap-2">
        <p className="desktop:block hidden text-[16px] leading-5">
          {t(price.key, price.params)}
        </p>
        <button
          type="button"
          className={cn(
            CLUB_CLASS.white,
            'desktop:h-12 h-11 desktop:w-auto w-full desktop:self-auto desktop:font-medium md:w-[360px] md:self-center',
          )}
          onClick={onJoin}
        >
          {t(cta.key, cta.params)}
        </button>
        <p
          className={cn(
            'desktop:hidden text-center text-[14px] leading-5',
            CLUB_CLASS.mutedOnDark,
          )}
        >
          {t(MEMBERSHIP_TEXT.clubPriceCancelAnytime, {
            price: t(shortPrice.key, shortPrice.params),
          })}
        </p>
      </div>
    </section>
  );
};

/**
 * Home banner for a signed-in non-member, opening Join. Client-only; holds
 * its space while `/me` and the plans load when it is likely to show;
 * reports `Entry Viewed` and `Entry Clicked` with `source: home`.
 */
export const ClubHomeBanner = observer(() => {
  const formatters = useMembershipFormatters();
  const plan = useClubOffer();
  const openJoin = useOpenClubJoin();
  const isReserved = useReservesClubEntry();
  const threshold = plan ? thresholdText(plan, formatters) : null;

  useViewedOnce(
    threshold ? entryPayload('home', plan) : null,
    reportEntryViewed,
  );

  if (!plan || !threshold) {
    return isReserved ? <ClubHomeBannerPlaceholder /> : null;
  }

  return (
    <ClubHomeBannerCard
      plan={plan}
      threshold={threshold}
      formatters={formatters}
      onJoin={() => openJoin('home', plan.code)}
    />
  );
});

/** Height of the profile promo card on mobile, held by its placeholder too. */
const PROFILE_PROMO_HEIGHT_CLASS = 'min-h-[150px]';

/**
 * Promo card of the mobile cabinet page for a non-member, below "Last orders"
 * across the content width (`order-2` slots it among the cabinet's ordered
 * sections): the member threshold, everyone else's, and a pill opening Join.
 * Reports `source: profile`.
 */
export const ClubProfilePromo = observer(() => {
  const { t } = useTranslation();
  const formatters = useMembershipFormatters();
  const plan = useClubOffer();
  const openJoin = useOpenClubJoin();
  const isReserved = useReservesClubEntry();
  const threshold = plan ? thresholdText(plan, formatters) : null;

  useViewedOnce(
    threshold ? entryPayload('profile', plan) : null,
    reportEntryViewed,
  );

  if (!plan || !threshold) {
    return isReserved ? (
      <div
        aria-hidden="true"
        className={cn(
          'order-2 mb-4 desktop:hidden animate-pulse rounded-[24px] bg-[#f2f2f2]',
          PROFILE_PROMO_HEIGHT_CLASS,
        )}
      />
    ) : null;
  }

  const price = clubPriceLine(plan, formatters);
  const cta = clubJoinCtaLine(plan);

  return (
    <button
      type="button"
      onClick={() => openJoin('profile', plan.code)}
      data-testid="club-profile-promo"
      className={cn(
        'relative order-2 mb-4 flex desktop:hidden cursor-pointer flex-col gap-4 overflow-hidden rounded-[24px] border-0 p-5 text-start text-white',
        PROFILE_PROMO_HEIGHT_CLASS,
        CLUB_CLASS.promo,
        CLUB_CLASS.font,
      )}
    >
      <ClubHeroFigure
        thresholdMinor={plan.freeDeliveryThresholdMinor}
        className="-right-4 -bottom-8 text-[150px]"
      />
      <span className="relative flex flex-col gap-1">
        <span className="font-semibold text-[20px] leading-[26px]">
          {t(
            plan.freeDeliveryThresholdMinor === 0
              ? MEMBERSHIP_TEXT.clubProfileTitleAlways
              : MEMBERSHIP_TEXT.clubProfileTitle,
            { threshold },
          )}
        </span>
        <span className={cn('text-[14px] leading-5', CLUB_CLASS.mutedOnDark)}>
          {t(MEMBERSHIP_TEXT.clubProfilePublic, {
            threshold: formatters.money(
              CLUB_PUBLIC_THRESHOLD_MINOR,
              plan.currency,
            ),
          })}
        </span>
      </span>
      <span className="relative flex flex-wrap items-center gap-3">
        <span
          className={cn(
            CLUB_CLASS.white,
            'h-11 px-5 text-[15px] leading-[18px]',
          )}
        >
          {t(cta.key, cta.params)}
        </span>
        <span className={cn('text-[14px] leading-5', CLUB_CLASS.mutedOnDark)}>
          {t(price.key, price.params)}
        </span>
      </span>
    </button>
  );
});

/** The short zone line of a member whose plan misses the current address. */
const ClubZoneMismatchLine = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  return (
    <p
      data-testid="club-zone-mismatch"
      className={cn(
        'm-0 text-[14px] leading-5',
        CLUB_CLASS.accentText,
        CLUB_CLASS.font,
        className,
      )}
    >
      {t(MEMBERSHIP_TEXT.cartZoneMismatch)}
    </p>
  );
};

/**
 * Upgrade "skirt" of a member whose plan misses the current zone: "Your Club
 * doesn't cover {plan}" naming the plan that covers it, and an "Upgrade" pill
 * opening the plan switch. On the checkout and the desktop cart the
 * panel's white edge cuts into the strip (`data-club-strip` drops the
 * checkout row's divider); the mobile cart, which has no panel, gets a plain
 * rounded strip.
 */
const ClubUpgradeSkirt = ({
  plan,
  source,
}: {
  plan: MembershipPlan;
  source: 'cart' | 'checkout';
}) => {
  const { t } = useTranslation();
  const openUpgrade = useOpenClubUpgrade();
  const isCart = source === 'cart';

  return (
    <div
      data-club-strip
      data-testid={`club-upgrade-skirt-${source}`}
      className={cn(
        'relative flex items-center justify-between gap-3 text-[14px] text-white leading-5',
        CLUB_CLASS.burgundy,
        CLUB_CLASS.font,
        isCart
          ? 'desktop:-mx-6 rounded-[16px] desktop:rounded-t-none desktop:rounded-b-[24px] desktop:px-6 px-4 py-3 desktop:pt-[26px] desktop:pb-[10px] desktop:text-[15px]'
          : '-mx-6 mt-1 -mb-4 rounded-b-[24px] px-6 pt-[26px] pb-[10px]',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'absolute inset-x-0 top-0 h-4 rounded-b-[16px] bg-white',
          isCart && 'max-desktop:hidden',
        )}
      />
      <span className="relative">
        {t(MEMBERSHIP_TEXT.clubUpgradeNotCovered, { planName: plan.name })}
      </span>
      <button
        type="button"
        onClick={() => openUpgrade(plan, source)}
        data-testid="club-upgrade-skirt-button"
        className={cn(
          'relative shrink-0 cursor-pointer rounded-full border-0 py-1 font-medium text-white [font-family:inherit]',
          CLUB_CLASS.accentBg,
          isCart
            ? 'px-3 desktop:text-[15px] text-[14px] leading-5'
            : 'px-[10px] text-[14px] leading-5',
        )}
      >
        {t(MEMBERSHIP_TEXT.clubUpgradeCta)}
      </button>
    </div>
  );
};

/**
 * Cart row under the totals for a customer with no membership whose paid
 * delivery the club would make free, as calculate says: "AED 0 delivery with
 * Club" and "Try free" or "Join" on the offered plan; reports `source: cart`.
 * A member whose plan misses the current zone gets the zone line instead.
 */
export const ClubCartRow = observer(() => {
  const { t } = useTranslation();
  const offer = useClubDeliveryOffer();
  const isZoneMismatch = useCartZoneMismatch();
  const upgrade = useClubUpgradeOffer();
  const openJoin = useOpenClubJoin();

  useViewedOnce(
    offer ? { source: 'cart' as const, planCode: offer.planCode } : null,
    reportEntryViewed,
  );

  if (isZoneMismatch) {
    return upgrade ? (
      <ClubUpgradeSkirt plan={upgrade} source="cart" />
    ) : (
      <ClubZoneMismatchLine className="-mt-2" />
    );
  }

  if (!offer) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => openJoin('cart', offer.planCode, offer.planId)}
      data-testid="club-cart-row"
      className={cn(
        '-mt-2 flex w-full cursor-pointer items-center justify-between gap-3 border-0 bg-transparent p-0 text-[14px] leading-5',
        CLUB_CLASS.accentText,
        CLUB_CLASS.font,
      )}
    >
      <span>
        {t(MEMBERSHIP_TEXT.clubCartRow, { amount: formatCartMoney(0) })}
      </span>
      <span className="font-medium">
        {t(clubCartCtaKey(membershipStore.view, offer))}
      </span>
    </button>
  );
});

/**
 * Checkout "skirt" under the delivery fee, across the totals panel: the
 * panel's white edge cuts into a burgundy strip with rounded bottom corners
 * (`data-club-strip` drops the row's divider in `Total.module.scss`). For a
 * customer with no membership whose delivery the club would make free, as
 * calculate says: "Save {fee} with CityDrinks Club" and "Join" on the offered
 * plan; reports `source: checkout`. A member whose plan misses the current
 * zone gets the zone line instead.
 */
export const ClubCheckoutRow = observer(() => {
  const { t } = useTranslation();
  const offer = useClubDeliveryOffer();
  const isZoneMismatch = useCartZoneMismatch();
  const upgrade = useClubUpgradeOffer();
  const openJoin = useOpenClubJoin();

  useViewedOnce(
    offer ? { source: 'checkout' as const, planCode: offer.planCode } : null,
    reportEntryViewed,
  );

  if (isZoneMismatch) {
    return upgrade ? (
      <ClubUpgradeSkirt plan={upgrade} source="checkout" />
    ) : (
      <ClubZoneMismatchLine className="mt-3" />
    );
  }

  if (!offer) {
    return null;
  }

  return (
    <div
      data-club-strip
      data-testid="club-checkout-strip"
      className={cn(
        'relative -mx-6 mt-1 -mb-4 flex items-center justify-between gap-3 rounded-b-[24px] px-6 pt-[26px] pb-[10px] text-white',
        CLUB_CLASS.burgundy,
        CLUB_CLASS.font,
      )}
    >
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-4 rounded-b-[16px] bg-white"
      />
      <span className="text-[14px] leading-5">
        {t(MEMBERSHIP_TEXT.clubCheckoutRow, {
          amount: formatCartMoney(offer.feeMinor),
        })}
      </span>
      <button
        type="button"
        onClick={() => openJoin('checkout', offer.planCode, offer.planId)}
        data-testid="club-checkout-join"
        className={cn(
          'cursor-pointer rounded-full border-0 px-[10px] py-1 font-medium text-[14px] text-white leading-5 [font-family:inherit]',
          CLUB_CLASS.accentBg,
        )}
      >
        {t(MEMBERSHIP_TEXT.clubJoinShort)}
      </button>
    </div>
  );
});

/**
 * Mobile title of the club page: "CityDrinks Club" and, for a member with
 * active benefits, the green "Active" badge next to it.
 */
export const ClubPageTitle = observer(() => {
  const { t } = useTranslation();
  const isMember = useIsClubMember();

  return (
    <span className="inline-flex items-center gap-3">
      {t(MEMBERSHIP_TEXT.title)}
      {isMember && (
        <span
          data-testid="club-active-badge"
          className={cn(CLUB_CLASS.badge, CLUB_CLASS.font)}
        >
          {t(MEMBERSHIP_TEXT.activeBadge)}
        </span>
      )}
    </span>
  );
});

/** Dot on the profile tab of a member with active benefits: 10px, orange gradient, a ring of the bar colour. */
export const ClubMemberDot = observer(() => {
  const isMember = useIsClubMember();

  if (!isMember) {
    return null;
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        'absolute top-1 right-1 size-2.5 rounded-full shadow-[0_0_0_2px_#f7f6f6]',
        CLUB_CLASS.orange,
      )}
    />
  );
});
